import React, { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Player,
  GolfRound,
  Course,
  TeeInfo,
  Signup,
  RoundGroup,
} from "../types";
import {
  generateBalancedGroups,
  calculateCourseHandicap,
} from "../src/utils/groupingLogic";

interface AdminViewProps {
  players: Player[];
  rounds: GolfRound[];
  courses: Course[];
  signups: Signup[];
  onUpdatePlayers: (players: Player[]) => void;
  onUpdateRounds: (rounds: GolfRound[]) => void;
  onUpdateCourses: (courses: Course[]) => void;
  onUpdateSignups: (signups: Signup[]) => void;
  authenticatedUserPhone?: string;
}

export const AdminView: React.FC<AdminViewProps> = ({
  players,
  rounds,
  courses,
  signups,
  onUpdatePlayers,
  onUpdateRounds,
  onUpdateCourses,
  onUpdateSignups,
  authenticatedUserPhone,
}) => {
  const [activeTab, setActiveTab] = useState<"rounds" | "players" | "courses">(
    "rounds",
  );
  const [addPlayerState, setAddPlayerState] = useState<
    Record<string, { playerId: string; teeId: string }>
  >({});

  // Round Form State
  const [courseId, setCourseId] = useState("");
  const [assignedTeeId, setAssignedTeeId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [buyIn, setBuyIn] = useState("");
  const [maxPlayers, setMaxPlayers] = useState("16");

  // Player Form State
  const [playerName, setPlayerName] = useState("");
  const [handicap, setHandicap] = useState<string>("");
  const [ghinNumber, setGhinNumber] = useState("");
  const [playerPhone, setPlayerPhone] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminPin, setAdminPin] = useState("");

  // Course Form State
  const [newCourseName, setNewCourseName] = useState("");

  // Tee Management State
  const [teeInputs, setTeeInputs] = useState<
    Record<
      string,
      {
        name: string;
        rating: string;
        slope: string;
        par: string;
        yardage: string;
      }
    >
  >({});

  // Edit Player State
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);
  const [editPlayerForm, setEditPlayerForm] = useState({
    name: "",
    phoneNumber: "",
    handicapIndex: "",
    ghinNumber: "",
    isAdmin: false,
    pin: "",
  });

  // Edit Round State
  const [editingRound, setEditingRound] = useState<GolfRound | null>(null);
  const [editRoundForm, setEditRoundForm] = useState({
    courseId: "",
    assignedTeeId: "",
    date: "",
    time: "",
    buyIn: "",
    maxPlayers: "16",
  });

  // Edit Course State
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [editCourseForm, setEditCourseForm] = useState<{
    name: string;
    tees: TeeInfo[];
  }>({ name: "", tees: [] });

  // Print State
  const [printingRoundId, setPrintingRoundId] = useState<string | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);

  const adminCount = useMemo(
    () => players.filter((p) => p.isAdmin).length,
    [players],
  );

  const { upcomingRounds, completedRounds } = useMemo(() => {
    const now = new Date();
    const today = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    ).getTime();

    const sorted = [...rounds].sort((a, b) => {
      const dateA = new Date(a.date + "T00:00:00").getTime();
      const dateB = new Date(b.date + "T00:00:00").getTime();
      return dateB - dateA;
    });

    const upcoming: GolfRound[] = [];
    const completed: GolfRound[] = [];

    sorted.forEach((r) => {
      const rTime = new Date(r.date + "T00:00:00").getTime();
      if (rTime < today) {
        completed.push(r);
      } else {
        upcoming.push(r);
      }
    });

    upcoming.reverse(); // Sort upcoming rounds ascending (soonest first)

    return { upcomingRounds: upcoming, completedRounds: completed };
  }, [rounds]);

  const handleAddRound = (e: React.FormEvent) => {
    e.preventDefault();
    if (!courseId || !date || !time) return;
    const newRound: GolfRound = {
      id: Math.random().toString(36).substr(2, 9),
      courseId,
      date,
      time,
      maxPlayers: parseInt(maxPlayers) || 16,
      buyIn: buyIn ? parseInt(buyIn) : undefined,
      assignedTeeId: assignedTeeId || undefined,
    };
    onUpdateRounds([...rounds, newRound]);
    setCourseId("");
    setAssignedTeeId("");
    setDate("");
    setTime("");
    setBuyIn("");
    setMaxPlayers("16");
  };

  const handleCreateCourseShell = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCourseName.trim()) return;

    if (
      courses.some(
        (c: Course) =>
          c.name.toLowerCase() === newCourseName.toLowerCase().trim(),
      )
    ) {
      alert("This course already exists.");
      return;
    }

    const newCourse: Course = {
      id: Math.random().toString(36).substr(2, 9),
      name: newCourseName.trim(),
      tees: [],
    };

    onUpdateCourses([...courses, newCourse]);
    setNewCourseName("");
  };

  const handleTeeInputChange = (
    courseId: string,
    field: string,
    value: string,
  ) => {
    setTeeInputs((prev) => ({
      ...prev,
      [courseId]: {
        ...(prev[courseId] || {
          name: "",
          rating: "",
          slope: "",
          par: "",
          yardage: "",
        }),
        [field]: value,
      },
    }));
  };

  const handleAddTeeToCourse = (courseId: string) => {
    const inputs = teeInputs[courseId];
    if (
      !inputs ||
      !inputs.name ||
      !inputs.rating ||
      !inputs.slope ||
      !inputs.par ||
      !inputs.yardage
    )
      return;

    const newTee: TeeInfo = {
      id: Math.random().toString(36).substr(2, 9),
      name: inputs.name,
      rating: parseFloat(inputs.rating),
      slope: parseFloat(inputs.slope),
      par: parseInt(inputs.par),
      yardage: parseInt(inputs.yardage),
    };

    const updatedCourses = courses.map((c) => {
      if (c.id === courseId) {
        return { ...c, tees: [...c.tees, newTee] };
      }
      return c;
    });

    onUpdateCourses(updatedCourses);

    // Reset inputs for this course
    setTeeInputs((prev) => ({
      ...prev,
      [courseId]: { name: "", rating: "", slope: "", par: "", yardage: "" },
    }));
  };

  const handleDeleteTee = (courseId: string, teeId: string) => {
    if (!window.confirm("Delete this tee marker?")) return;
    const updatedCourses = courses.map((c) => {
      if (c.id === courseId) {
        return { ...c, tees: c.tees.filter((t) => t.id !== teeId) };
      }
      return c;
    });
    onUpdateCourses(updatedCourses);
  };

  const handleAddPlayer = (e: React.FormEvent) => {
    e.preventDefault();
    if (!playerName) return;

    let formattedPhone = playerPhone.trim().replace(/\s+/g, "");
    if (formattedPhone && !formattedPhone.startsWith("+")) {
      const digitsOnly = formattedPhone.replace(/\D/g, "");
      formattedPhone =
        digitsOnly.length === 10 ? `+1${digitsOnly}` : `+${digitsOnly}`;
    }

    const newPlayer: Player = {
      id: Math.random().toString(36).substr(2, 9),
      name: playerName,
      handicapIndex: handicap ? parseFloat(handicap) : null,
      ghinNumber: ghinNumber.trim() || undefined,
      phoneNumber: formattedPhone || undefined,
      isAdmin: isAdmin,
      pin: isAdmin ? adminPin || "0000" : undefined,
    };
    onUpdatePlayers([...players, newPlayer]);
    setPlayerName("");
    setHandicap("");
    setGhinNumber("");
    setPlayerPhone("");
    setIsAdmin(false);
    setAdminPin("");
  };

  const handleToggleAdmin = (playerId: string) => {
    const player = players.find((p) => p.id === playerId);
    if (player?.isAdmin && adminCount === 1) {
      alert("At least one administrator is required.");
      return;
    }

    if (!player?.isAdmin) {
      const pin = prompt("Set a 4-digit PIN for this new admin:", "0000");
      if (pin === null) return; // Cancelled
      onUpdatePlayers(
        players.map((p: Player) =>
          p.id === playerId ? { ...p, isAdmin: true, pin: pin || "0000" } : p,
        ),
      );
    } else {
      onUpdatePlayers(
        players.map((p: Player) =>
          p.id === playerId ? { ...p, isAdmin: false, pin: undefined } : p,
        ),
      );
    }
  };

  const handleChangePin = (playerId: string) => {
    const pin = prompt("Enter new 4-digit PIN:");
    if (pin) {
      onUpdatePlayers(
        players.map((p: Player) =>
          p.id === playerId ? { ...p, pin: pin } : p,
        ),
      );
    }
  };

  const handleDeletePlayer = (playerId: string) => {
    const player = players.find((p) => p.id === playerId);
    if (player?.isAdmin && adminCount === 1) {
      alert("Cannot delete the only administrator.");
      return;
    }
    if (window.confirm(`Delete ${player?.name} from roster?`)) {
      onUpdatePlayers(players.filter((p: Player) => p.id !== playerId));
    }
  };

  const handleEditPlayer = (player: Player) => {
    setEditingPlayer(player);
    setEditPlayerForm({
      name: player.name,
      phoneNumber: player.phoneNumber || "",
      handicapIndex: player.handicapIndex?.toString() || "",
      ghinNumber: player.ghinNumber || "",
      isAdmin: !!player.isAdmin,
      pin: player.pin || "",
    });
  };

  const handleSaveEditPlayer = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPlayer) return;

    let formattedPhone = editPlayerForm.phoneNumber.trim().replace(/\s+/g, "");
    if (formattedPhone && !formattedPhone.startsWith("+")) {
      const digitsOnly = formattedPhone.replace(/\D/g, "");
      formattedPhone =
        digitsOnly.length === 10 ? `+1${digitsOnly}` : `+${digitsOnly}`;
    }

    const updatedPlayer: Player = {
      ...editingPlayer,
      name: editPlayerForm.name,
      phoneNumber: formattedPhone || undefined,
      handicapIndex: editPlayerForm.handicapIndex
        ? parseFloat(editPlayerForm.handicapIndex)
        : null,
      ghinNumber: editPlayerForm.ghinNumber.trim() || undefined,
      isAdmin: editPlayerForm.isAdmin,
      pin: editPlayerForm.isAdmin ? editPlayerForm.pin || "0000" : undefined,
    };

    onUpdatePlayers(
      players.map((p) => (p.id === editingPlayer.id ? updatedPlayer : p)),
    );
    setEditingPlayer(null);
  };

  const handleEditRound = (round: GolfRound) => {
    setEditingRound(round);
    setEditRoundForm({
      courseId: round.courseId,
      assignedTeeId: round.assignedTeeId || "",
      date: round.date,
      time: round.time,
      buyIn: round.buyIn ? round.buyIn.toString() : "",
      maxPlayers: round.maxPlayers?.toString() || "16",
    });
  };

  const handleSaveEditRound = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRound) return;

    const updatedRound: GolfRound = {
      ...editingRound,
      courseId: editRoundForm.courseId,
      assignedTeeId: editRoundForm.assignedTeeId || undefined,
      date: editRoundForm.date,
      time: editRoundForm.time,
      buyIn: editRoundForm.buyIn ? parseInt(editRoundForm.buyIn) : undefined,
      maxPlayers: parseInt(editRoundForm.maxPlayers) || 16,
    };

    if (
      updatedRound.assignedTeeId &&
      updatedRound.assignedTeeId !== editingRound.assignedTeeId
    ) {
      const updatedSignups = signups.map((s) => {
        if (s.roundId === editingRound.id && s.status === "in") {
          return { ...s, teeId: updatedRound.assignedTeeId };
        }
        return s;
      });
      onUpdateSignups(updatedSignups);
    }

    onUpdateRounds(
      rounds.map((r) => (r.id === editingRound.id ? updatedRound : r)),
    );
    setEditingRound(null);
  };

  const handleEditCourse = (course: Course) => {
    setEditingCourse(course);
    setEditCourseForm({
      name: course.name,
      tees: JSON.parse(JSON.stringify(course.tees)),
    });
  };

  const handleSaveEditCourse = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCourse) return;

    const updatedCourse: Course = {
      ...editingCourse,
      name: editCourseForm.name,
      tees: editCourseForm.tees.map((t) => ({
        ...t,
        rating: Number(t.rating),
        slope: Number(t.slope),
        par: Number(t.par),
        yardage: t.yardage ? Number(t.yardage) : undefined,
      })),
    };

    onUpdateCourses(
      courses.map((c) => (c.id === editingCourse.id ? updatedCourse : c)),
    );
    setEditingCourse(null);
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (
      parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase();
  };

  const formatPhoneNumber = (phone: string | undefined) => {
    if (!phone) return "No Phone";
    const cleaned = ("" + phone).replace(/\D/g, "");
    const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[2]}) ${match[3]}-${match[4]}`;
    }
    return phone;
  };

  const handleAddPlayerToRound = (
    roundId: string,
    playerId: string,
    explicitTeeId?: string,
  ) => {
    const round = rounds.find((r) => r.id === roundId);
    if (!round) return;

    const roundSignups = signups.filter(
      (s) => s.roundId === roundId && s.status === "in",
    );
    const isFull = roundSignups.length >= round.maxPlayers;

    const existingSignupIndex = signups.findIndex(
      (s) => s.roundId === roundId && s.playerId === playerId,
    );

    if (existingSignupIndex !== -1) {
      const updatedSignups = [...signups];
      updatedSignups[existingSignupIndex] = {
        ...updatedSignups[existingSignupIndex],
        status: isFull ? "waitlist" : "in",
        teeId: explicitTeeId || round.assignedTeeId || undefined,
        updatedAt: Date.now(),
      };
      onUpdateSignups(updatedSignups);
    } else {
      onUpdateSignups([
        ...signups,
        {
          roundId,
          playerId,
          status: isFull ? "waitlist" : "in",
          teeId: explicitTeeId || round.assignedTeeId || undefined,
          updatedAt: Date.now(),
        },
      ]);
    }
  };

  const handlePromotePlayer = (roundId: string, playerId: string) => {
    const signupIndex = signups.findIndex(
      (s) => s.roundId === roundId && s.playerId === playerId,
    );
    if (signupIndex !== -1) {
      const updatedSignups = [...signups];
      updatedSignups[signupIndex] = {
        ...updatedSignups[signupIndex],
        status: "in",
        updatedAt: Date.now(),
      };
      onUpdateSignups(updatedSignups);
    }
  };

  const handleRemovePlayerFromRound = (roundId: string, playerId: string) => {
    if (!window.confirm("Remove this player from the round?")) return;

    const signupIndex = signups.findIndex(
      (s) => s.roundId === roundId && s.playerId === playerId,
    );

    if (signupIndex !== -1) {
      const updatedSignups = [...signups];
      updatedSignups[signupIndex] = {
        ...updatedSignups[signupIndex],
        status: "out",
        updatedAt: Date.now(),
      };
      onUpdateSignups(updatedSignups);
    }

    const round = rounds.find((r) => r.id === roundId);
    if (round && round.groups) {
      const newGroups = round.groups.map((g) => ({
        ...g,
        playerIds: g.playerIds.filter((id) => id !== playerId),
      }));

      const newPrevGroups = round.previousGroups?.map((g) => ({
        ...g,
        playerIds: g.playerIds.filter((id) => id !== playerId),
      }));

      const updatedRound: GolfRound = {
        ...round,
        groups: newGroups,
        previousGroups:
          newPrevGroups ||
          (round.groupingState === "published" ? newGroups : undefined),
        groupingState: "draft",
      };

      onUpdateRounds(rounds.map((r) => (r.id === roundId ? updatedRound : r)));
    }
  };

  const handleGenerateGroups = (roundId: string) => {
    const round = rounds.find((r) => r.id === roundId);
    if (!round) return;

    const course = courses.find((c) => c.id === round.courseId);
    const roundSignups = signups.filter(
      (s) => s.roundId === roundId && s.status === "in",
    );

    const newGroups = generateBalancedGroups(roundSignups, players, course);

    const updatedRound: GolfRound = {
      ...round,
      groupingState: "draft",
      groups: newGroups,
      previousGroups:
        round.groupingState === "published"
          ? round.groups
          : round.previousGroups,
    };

    onUpdateRounds(rounds.map((r) => (r.id === roundId ? updatedRound : r)));
  };

  const handleCancelGroups = (roundId: string) => {
    const round = rounds.find((r) => r.id === roundId);
    if (!round) return;

    const updatedRound: GolfRound = {
      ...round,
      groupingState: round.previousGroups ? "published" : "unassigned",
      groups: round.previousGroups || undefined,
      previousGroups: undefined,
    };

    onUpdateRounds(rounds.map((r) => (r.id === roundId ? updatedRound : r)));
  };

  const handleEditGroups = (roundId: string) => {
    const round = rounds.find((r) => r.id === roundId);
    if (!round) return;

    const updatedRound: GolfRound = {
      ...round,
      groupingState: "draft",
      previousGroups: round.groups,
    };

    onUpdateRounds(rounds.map((r) => (r.id === roundId ? updatedRound : r)));
  };

  const handlePublishGroups = (roundId: string) => {
    const round = rounds.find((r) => r.id === roundId);
    if (!round) return;

    const updatedRound: GolfRound = {
      ...round,
      groupingState: "published",
      previousGroups: undefined,
    };

    onUpdateRounds(rounds.map((r) => (r.id === roundId ? updatedRound : r)));
  };

  const handleMovePlayer = (
    roundId: string,
    playerId: string,
    targetGroupId: string,
  ) => {
    const round = rounds.find((r) => r.id === roundId);
    if (!round || !round.groups) return;

    const newGroups = round.groups.map((g) => ({
      ...g,
      playerIds: g.playerIds.filter((id) => id !== playerId),
    }));

    if (targetGroupId !== "unassigned") {
      const targetGroup = newGroups.find((g) => g.id === targetGroupId);
      if (targetGroup) {
        targetGroup.playerIds.push(playerId);
      }
    }

    onUpdateRounds(
      rounds.map((r) => (r.id === roundId ? { ...r, groups: newGroups } : r)),
    );
  };

  const renderEditPlayerModal = () => {
    if (!editingPlayer) return null;

    return createPortal(
      <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
          <div className="bg-emerald-700 p-6 flex justify-between items-center">
            <h3 className="text-white font-black uppercase tracking-widest text-sm">
              Edit Player Profile
            </h3>
            <button
              onClick={() => setEditingPlayer(null)}
              className="text-emerald-200 hover:text-white transition-colors text-xl"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSaveEditPlayer} className="p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={editPlayerForm.name}
                  onChange={(e) =>
                    setEditPlayerForm({
                      ...editPlayerForm,
                      name: e.target.value,
                    })
                  }
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Mobile Phone
                </label>
                <input
                  type="tel"
                  value={editPlayerForm.phoneNumber}
                  onChange={(e) =>
                    setEditPlayerForm({
                      ...editPlayerForm,
                      phoneNumber: e.target.value,
                    })
                  }
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Handicap Index
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={editPlayerForm.handicapIndex}
                  onChange={(e) =>
                    setEditPlayerForm({
                      ...editPlayerForm,
                      handicapIndex: e.target.value,
                    })
                  }
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  GHIN #
                </label>
                <input
                  type="text"
                  value={editPlayerForm.ghinNumber}
                  onChange={(e) =>
                    setEditPlayerForm({
                      ...editPlayerForm,
                      ghinNumber: e.target.value,
                    })
                  }
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                />
              </div>
              <div className="space-y-2 md:col-span-2 flex items-center gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="editIsAdmin"
                    checked={editPlayerForm.isAdmin}
                    onChange={(e) =>
                      setEditPlayerForm({
                        ...editPlayerForm,
                        isAdmin: e.target.checked,
                      })
                    }
                    className="w-5 h-5 rounded text-emerald-600 focus:ring-emerald-500"
                  />
                  <label
                    htmlFor="editIsAdmin"
                    className="text-xs font-black text-slate-700 uppercase tracking-widest"
                  >
                    Administrator Access
                  </label>
                </div>
                {editPlayerForm.isAdmin && (
                  <input
                    type="text"
                    maxLength={4}
                    placeholder="PIN"
                    value={editPlayerForm.pin}
                    onChange={(e) =>
                      setEditPlayerForm({
                        ...editPlayerForm,
                        pin: e.target.value,
                      })
                    }
                    className="flex-1 p-2 bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500/20 text-sm font-black text-slate-700 text-center tracking-[0.2em]"
                    required
                  />
                )}
              </div>
            </div>
            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={() => setEditingPlayer(null)}
                className="flex-1 bg-slate-100 text-slate-500 font-black py-4 rounded-2xl hover:bg-slate-200 transition-all uppercase text-xs tracking-widest"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 bg-emerald-700 text-white font-black py-4 rounded-2xl hover:bg-emerald-800 shadow-xl transition-all uppercase text-xs tracking-widest"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>,
      document.body,
    );
  };

  const renderEditRoundModal = () => {
    if (!editingRound) return null;

    return createPortal(
      <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
          <div className="bg-emerald-700 p-6 flex justify-between items-center">
            <h3 className="text-white font-black uppercase tracking-widest text-sm">
              Edit Round
            </h3>
            <button
              onClick={() => setEditingRound(null)}
              className="text-emerald-200 hover:text-white transition-colors text-xl"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSaveEditRound} className="p-8 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Select Course
                </label>
                <select
                  value={editRoundForm.courseId}
                  onChange={(e) =>
                    setEditRoundForm({
                      ...editRoundForm,
                      courseId: e.target.value,
                      assignedTeeId: "",
                    })
                  }
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                  required
                >
                  <option value="">Choose Course...</option>
                  {courses.map((c: Course) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Assigned Tee
                </label>
                <select
                  value={editRoundForm.assignedTeeId}
                  onChange={(e) =>
                    setEditRoundForm({
                      ...editRoundForm,
                      assignedTeeId: e.target.value,
                    })
                  }
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                  disabled={!editRoundForm.courseId}
                >
                  <option value="">Open</option>
                  {editRoundForm.courseId &&
                    courses
                      .find((c) => c.id === editRoundForm.courseId)
                      ?.tees.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Play Date
                </label>
                <input
                  type="date"
                  value={editRoundForm.date}
                  onChange={(e) =>
                    setEditRoundForm({ ...editRoundForm, date: e.target.value })
                  }
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Tee Time
                </label>
                <input
                  type="time"
                  value={editRoundForm.time}
                  onChange={(e) =>
                    setEditRoundForm({ ...editRoundForm, time: e.target.value })
                  }
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                  required
                />
              </div>
              <div className="space-y-2 sm:col-span-1">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Buy-In ($)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-black">
                    $
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="Optional"
                    value={editRoundForm.buyIn}
                    onChange={(e) =>
                      setEditRoundForm({
                        ...editRoundForm,
                        buyIn: e.target.value,
                      })
                    }
                    className="w-full p-4 pl-8 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                  />
                </div>
              </div>
              <div className="space-y-2 sm:col-span-1">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Max Players
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  required
                  value={editRoundForm.maxPlayers}
                  onChange={(e) =>
                    setEditRoundForm({
                      ...editRoundForm,
                      maxPlayers: e.target.value,
                    })
                  }
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                />
              </div>
            </div>
            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={() => setEditingRound(null)}
                className="flex-1 bg-slate-100 text-slate-500 font-black py-4 rounded-2xl hover:bg-slate-200 transition-all uppercase text-xs tracking-widest"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 bg-emerald-700 text-white font-black py-4 rounded-2xl hover:bg-emerald-800 shadow-xl transition-all uppercase text-xs tracking-widest"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>,
      document.body,
    );
  };

  const renderEditCourseModal = () => {
    if (!editingCourse) return null;

    return createPortal(
      <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200 flex flex-col max-h-[90vh]">
          <div className="bg-emerald-700 p-6 flex justify-between items-center flex-shrink-0">
            <h3 className="text-white font-black uppercase tracking-widest text-sm">
              Edit Course
            </h3>
            <button
              onClick={() => setEditingCourse(null)}
              className="text-emerald-200 hover:text-white transition-colors text-xl"
            >
              ✕
            </button>
          </div>

          <form
            onSubmit={handleSaveEditCourse}
            className="p-8 space-y-6 overflow-y-auto flex-grow"
          >
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                Course Name
              </label>
              <input
                type="text"
                value={editCourseForm.name}
                onChange={(e) =>
                  setEditCourseForm({ ...editCourseForm, name: e.target.value })
                }
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                required
              />
            </div>

            <div className="space-y-4">
              <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">
                Edit Tee Markers
              </h4>
              {editCourseForm.tees.length === 0 ? (
                <p className="text-xs text-slate-400 italic">
                  No tees to edit.
                </p>
              ) : (
                editCourseForm.tees.map((tee, index) => (
                  <div
                    key={index}
                    className="bg-slate-50 p-4 rounded-xl border border-slate-100 space-y-3"
                  >
                    <div className="space-y-1">
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                        Tee Name
                      </label>
                      <input
                        type="text"
                        value={tee.name}
                        onChange={(e) => {
                          const newTees = [...editCourseForm.tees];
                          newTees[index].name = e.target.value;
                          setEditCourseForm({
                            ...editCourseForm,
                            tees: newTees,
                          });
                        }}
                        className="w-full p-2 bg-white border border-slate-200 rounded-lg text-xs font-bold outline-none focus:ring-2 focus:ring-emerald-500/20"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div className="space-y-1">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                          Yardage
                        </label>
                        <input
                          type="number"
                          value={tee.yardage || ""}
                          onChange={(e) => {
                            const newTees = [...editCourseForm.tees];
                            newTees[index].yardage = parseInt(e.target.value);
                            setEditCourseForm({
                              ...editCourseForm,
                              tees: newTees,
                            });
                          }}
                          className="w-full p-2 bg-white border border-slate-200 rounded-lg text-xs font-bold outline-none focus:ring-2 focus:ring-emerald-500/20"
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                          Rating
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          value={tee.rating}
                          onChange={(e) => {
                            const newTees = [...editCourseForm.tees];
                            newTees[index].rating = parseFloat(e.target.value);
                            setEditCourseForm({
                              ...editCourseForm,
                              tees: newTees,
                            });
                          }}
                          className="w-full p-2 bg-white border border-slate-200 rounded-lg text-xs font-bold outline-none focus:ring-2 focus:ring-emerald-500/20"
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                          Slope
                        </label>
                        <input
                          type="number"
                          value={tee.slope}
                          onChange={(e) => {
                            const newTees = [...editCourseForm.tees];
                            newTees[index].slope = parseFloat(e.target.value);
                            setEditCourseForm({
                              ...editCourseForm,
                              tees: newTees,
                            });
                          }}
                          className="w-full p-2 bg-white border border-slate-200 rounded-lg text-xs font-bold outline-none focus:ring-2 focus:ring-emerald-500/20"
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                          Par
                        </label>
                        <input
                          type="number"
                          value={tee.par}
                          onChange={(e) => {
                            const newTees = [...editCourseForm.tees];
                            newTees[index].par = parseInt(e.target.value);
                            setEditCourseForm({
                              ...editCourseForm,
                              tees: newTees,
                            });
                          }}
                          className="w-full p-2 bg-white border border-slate-200 rounded-lg text-xs font-bold outline-none focus:ring-2 focus:ring-emerald-500/20"
                          required
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="flex gap-4 pt-4 flex-shrink-0">
              <button
                type="button"
                onClick={() => setEditingCourse(null)}
                className="flex-1 bg-slate-100 text-slate-500 font-black py-4 rounded-2xl hover:bg-slate-200 transition-all uppercase text-xs tracking-widest"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 bg-emerald-700 text-white font-black py-4 rounded-2xl hover:bg-emerald-800 shadow-xl transition-all uppercase text-xs tracking-widest"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>,
      document.body,
    );
  };

  // --- PRINT PREVIEW PORTAL RENDERER ---
  const renderPrintPreview = () => {
    if (!printingRoundId) return null;

    const round = rounds.find((r) => r.id === printingRoundId);
    if (!round) {
      // Self-correct if round ID is invalid
      setPrintingRoundId(null);
      return null;
    }

    const course = courses.find((c: Course) => c.id === round.courseId);
    const attendees = signups.filter(
      (s: Signup) => s.roundId === round.id && s.status === "in",
    );

    return createPortal(
      <div className="print-portal-wrapper fixed inset-0 z-[9999] bg-white overflow-y-auto">
        <style>{`
          @media print {
            /* Force reset body/html to allow full flow */
            html, body {
              height: auto !important;
              overflow: visible !important;
              margin: 0 !important;
              padding: 0 !important;
              background: white !important;
            }
            
            /* Hide the main app root entirely */
            #root {
              display: none !important;
            }

            /* Make the portal wrapper the main flow content (static position) */
            .print-portal-wrapper {
              position: static !important;
              display: block !important;
              width: 100% !important;
              height: auto !important;
              overflow: visible !important;
              background: white !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            
            /* Ensure visibility of all children */
            .print-portal-wrapper * {
              visibility: visible !important;
            }
          }
        `}</style>

        {/* Navigation Bar - Hidden on Print */}
        <div className="print:hidden sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-slate-200 p-4 flex justify-between items-center shadow-sm pt-safe">
          <button
            onClick={() => setPrintingRoundId(null)}
            className="flex items-center gap-2 text-slate-500 hover:text-emerald-700 transition-colors"
          >
            <span className="text-xl">←</span>
            <span className="text-xs font-black uppercase tracking-widest">
              Back
            </span>
          </button>
          <span className="text-xs font-black uppercase tracking-[0.2em] text-slate-300">
            Print Preview
          </span>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 bg-emerald-700 text-white px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-800 transition-all shadow-lg active:scale-95"
          >
            <span>🖨️</span> Print Page
          </button>
        </div>

        {/* Printable Content */}
        <div className="max-w-3xl mx-auto p-6 sm:p-10 print:p-0 print:w-full print:max-w-none">
          <div className="flex justify-between items-start mb-10 border-b-4 border-slate-900 pb-6">
            <div>
              <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tighter uppercase mb-3">
                {course?.name || "Golf Round"}
              </h1>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-sm font-bold text-slate-500 uppercase tracking-widest">
                <span className="flex items-center gap-2">
                  {new Date(round.date + "T00:00:00").toLocaleDateString(
                    undefined,
                    { weekday: "long", month: "long", day: "numeric" },
                  )}
                </span>
                <span className="hidden sm:inline text-slate-300">•</span>
                <span className="flex items-center gap-2">{round.time}</span>
              </div>
            </div>
            <div className="text-right hidden sm:block">
              <img
                src="/logo-login.png"
                alt="Tee Time Connect"
                className="h-16 object-contain"
              />
            </div>
          </div>

          {round.groupingState === "published" && round.groups ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 print:grid-cols-2 print:gap-6">
              {round.groups.map((group) => {
                const groupPlayers = group.playerIds
                  .map((pid) => {
                    const p = players.find((p) => p.id === pid);
                    const s = signups.find(
                      (s) => s.playerId === pid && s.roundId === round.id,
                    );
                    const t = course?.tees.find((t) => t.id === s?.teeId);
                    const ch = calculateCourseHandicap(
                      p?.handicapIndex || null,
                      t,
                    );
                    return { player: p, signup: s, tee: t, ch };
                  })
                  .filter((x) => x.player && x.signup?.status === "in");

                return (
                  <div
                    key={group.id}
                    className="border border-slate-200 rounded-lg overflow-hidden break-inside-avoid"
                  >
                    <div className="bg-slate-100 px-4 py-2 border-b border-slate-200">
                      <h4 className="font-black text-slate-800 uppercase tracking-widest text-xs">
                        {group.name}
                      </h4>
                    </div>
                    <table className="w-full text-left text-sm">
                      <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-widest text-[10px] border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-2 w-1/2">Player</th>
                          <th className="px-4 py-2 w-1/4">Tee</th>
                          <th className="px-4 py-2 w-1/4 text-center">C.H.</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                        {groupPlayers.map(({ player, tee, ch }) => (
                          <tr key={player!.id}>
                            <td className="px-4 py-2 font-black text-slate-900 text-xs uppercase">
                              {player!.name}
                            </td>
                            <td className="px-4 py-2 text-[10px] uppercase font-bold text-slate-500">
                              {tee?.name || "-"}
                            </td>
                            <td className="px-4 py-2 text-center font-black text-xs text-slate-900">
                              {ch ?? "-"}
                            </td>
                          </tr>
                        ))}
                        {[...Array(Math.max(0, 4 - groupPlayers.length))].map(
                          (_, i) => (
                            <tr key={`empty-${i}`} className="h-8">
                              <td className="px-4 py-2 border-b border-slate-100 border-dashed"></td>
                              <td className="px-4 py-2 border-b border-slate-100 border-dashed"></td>
                              <td className="px-4 py-2 border-b border-slate-100 border-dashed"></td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="border border-slate-200 rounded-lg overflow-hidden print:border-0 print:rounded-none">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-100 text-slate-900 font-black uppercase tracking-widest border-b-2 border-slate-900 print:bg-transparent">
                  <tr>
                    <th className="px-6 py-2 w-5/12">Player</th>
                    <th className="px-6 py-2 w-3/12">Tee Box</th>
                    <th className="px-6 py-2 w-2/12 text-right">H.I.</th>
                    <th className="px-6 py-2 w-2/12 text-center bg-slate-200 print:bg-transparent">
                      C.H.
                    </th>
                    <th className="px-2 py-2 w-12 border-l border-slate-200"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 font-medium text-slate-700">
                  {attendees.length > 0 ? (
                    attendees.map((signup: Signup) => {
                      const player = players.find(
                        (p: Player) => p.id === signup.playerId,
                      );
                      const tee = course?.tees.find(
                        (t: TeeInfo) => t.id === signup.teeId,
                      );
                      const ch = calculateCourseHandicap(
                        player?.handicapIndex || null,
                        tee,
                      );
                      return (
                        <tr
                          key={signup.playerId}
                          className="break-inside-avoid"
                        >
                          <td className="px-6 py-2 font-black text-slate-900 text-xs uppercase">
                            {player?.name || "Unknown"}
                          </td>
                          <td className="px-6 py-2">
                            {tee ? (
                              <div>
                                <div className="font-bold uppercase text-xs text-slate-900">
                                  {tee.name}
                                </div>
                                <div className="text-[10px] text-slate-400 mt-0.5 font-bold tracking-wider">
                                  {tee.slope} / {tee.rating}
                                </div>
                              </div>
                            ) : (
                              <span className="text-slate-300">-</span>
                            )}
                          </td>
                          <td className="px-6 py-2 text-right font-mono text-slate-500">
                            {player?.handicapIndex ?? "-"}
                          </td>
                          <td className="px-6 py-2 text-center font-black text-xs text-slate-900 bg-slate-50 print:bg-transparent">
                            {ch ?? "-"}
                          </td>
                          <td className="px-2 py-2 border-l border-slate-100"></td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-12 text-center italic text-slate-400 font-bold"
                      >
                        No players on the roster yet.
                      </td>
                    </tr>
                  )}

                  {/* Fill rows for standard foursome if needed */}
                  {[...Array(Math.max(0, 4 - attendees.length))].map((_, i) => (
                    <tr key={`empty-${i}`} className="h-12 print:table-row">
                      <td className="px-6 py-2 border-b border-slate-100 border-dashed"></td>
                      <td className="px-6 py-2 border-b border-slate-100 border-dashed"></td>
                      <td className="px-6 py-2 border-b border-slate-100 border-dashed"></td>
                      <td className="px-6 py-2 border-b border-slate-100 border-dashed bg-slate-50/30"></td>
                      <td className="px-2 py-2 border-l border-slate-100"></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-12 pt-6 border-t-2 border-slate-100 flex justify-between text-[10px] uppercase font-bold text-slate-400 tracking-[0.2em]">
            <span>Tee Time Connect • Roster Sheet</span>
            <span>{new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </div>,
      document.body,
    );
  };

  const renderRoundCard = (round: GolfRound) => {
    const course = courses.find((c: Course) => c.id === round.courseId);
    const allRoundSignups = signups.filter(
      (s: Signup) => s.roundId === round.id,
    );
    const attendees = allRoundSignups.filter((s) => s.status === "in");

    const isGrouped =
      round.groupingState === "draft" || round.groupingState === "published";

    return (
      <div
        key={round.id}
        className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-100 space-y-6 group transition-all hover:border-emerald-200"
      >
        <div className="flex justify-between items-start">
          <div className="pl-2">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-black text-emerald-600 text-2xl tracking-tighter">
                {new Date(round.date + "T00:00:00").toLocaleDateString(
                  undefined,
                  { weekday: "long" },
                )}
              </span>
              <span className="font-black text-slate-900 text-2xl tracking-tighter">
                {new Date(round.date + "T00:00:00").toLocaleDateString(
                  undefined,
                  { month: "short", day: "numeric" },
                )}{" "}
                @ {round.time}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              <span className="font-bold text-slate-600 text-lg">
                {course?.name || "Unknown Course"}
              </span>
              {round.buyIn && (
                <>
                  <span className="text-xs font-bold text-slate-300">•</span>
                  <span className="text-xs font-bold text-emerald-600">
                    Buy-In: ${round.buyIn}
                  </span>
                </>
              )}
              {round.assignedTeeId && (
                <>
                  <span className="text-xs font-bold text-slate-300">•</span>
                  <span className="text-xs font-bold text-emerald-600">
                    Tee:{" "}
                    {
                      course?.tees.find((t) => t.id === round.assignedTeeId)
                        ?.name
                    }
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPrintingRoundId(round.id)}
              className="text-emerald-600 hover:text-emerald-800 p-2 font-black text-[10px] uppercase tracking-widest transition-colors flex items-center gap-1"
              title="Print Roster"
            >
              <span className="text-lg">🖨️</span>{" "}
              <span className="hidden sm:inline">Print</span>
            </button>
            <button
              onClick={() => handleEditRound(round)}
              className="text-slate-400 hover:text-emerald-600 p-2 transition-colors text-xl"
            >
              ✎
            </button>
            <button
              onClick={() =>
                onUpdateRounds(
                  rounds.filter((r: GolfRound) => r.id !== round.id),
                )
              }
              className="text-slate-400 hover:text-rose-600 p-3 transition-colors text-xl"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Grouping Controls */}
        <div className="flex flex-wrap gap-3 items-center justify-between border-t border-slate-50 pt-4">
          <div className="flex flex-wrap gap-3 items-center">
            {!isGrouped ? (
              <button
                onClick={() => handleGenerateGroups(round.id)}
                disabled={attendees.length < 3}
                className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${attendees.length < 3 ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200"}`}
              >
                Generate Groups
              </button>
            ) : (
              <div className="flex items-center gap-3">
                {round.groupingState === "draft" && (
                  <>
                    <button
                      onClick={() => handlePublishGroups(round.id)}
                      className="px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all bg-emerald-600 text-white hover:bg-emerald-700 shadow-md"
                    >
                      Publish Groups
                    </button>
                    <button
                      onClick={() => handleCancelGroups(round.id)}
                      className="px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700"
                    >
                      Cancel
                    </button>
                  </>
                )}
                {round.groupingState === "published" && (
                  <>
                    <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-600 pr-2 select-none">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Published
                    </div>
                    <button
                      onClick={() => handleGenerateGroups(round.id)}
                      className="px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
                    >
                      Regenerate Groups
                    </button>
                    <button
                      onClick={() => handleEditGroups(round.id)}
                      className="px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200"
                    >
                      Edit Groups
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <select
              value={addPlayerState[round.id]?.playerId || ""}
              onChange={(e) => {
                const playerId = e.target.value;
                if (round.assignedTeeId) {
                  handleAddPlayerToRound(round.id, playerId);
                  setAddPlayerState((prev) => {
                    const next = { ...prev };
                    delete next[round.id];
                    return next;
                  });
                } else {
                  setAddPlayerState((prev) => ({
                    ...prev,
                    [round.id]: { ...prev[round.id], playerId },
                  }));
                }
              }}
              className="text-[10px] font-bold uppercase tracking-widest bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg hover:bg-emerald-100 transition-colors outline-none cursor-pointer border border-emerald-100 appearance-none text-center"
            >
              <option value="" disabled>
                ADD PLAYER
              </option>
              {players
                .filter(
                  (p) =>
                    !allRoundSignups.find(
                      (s) =>
                        s.playerId === p.id &&
                        (s.status === "in" || s.status === "waitlist"),
                    ),
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>

            {!round.assignedTeeId && addPlayerState[round.id]?.playerId && (
              <select
                value=""
                onChange={(e) => {
                  const teeId = e.target.value;
                  const playerId = addPlayerState[round.id]?.playerId;
                  if (playerId && teeId) {
                    handleAddPlayerToRound(round.id, playerId, teeId);
                    setAddPlayerState((prev) => {
                      const next = { ...prev };
                      delete next[round.id];
                      return next;
                    });
                  }
                }}
                className="text-[10px] font-bold uppercase tracking-widest bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg hover:bg-emerald-100 transition-colors outline-none cursor-pointer border border-emerald-100 appearance-none text-center"
              >
                <option value="" disabled>
                  SELECT TEE
                </option>
                {course?.tees.map((t: TeeInfo) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {isGrouped && round.groups ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {round.groups.map((group) => {
                // Calculate average CH
                let totalCh = 0;
                let validChCount = 0;
                const groupPlayers = group.playerIds
                  .map((pid) => {
                    const p = players.find((p) => p.id === pid);
                    const s = allRoundSignups.find((s) => s.playerId === pid);
                    const t = course?.tees.find((t) => t.id === s?.teeId);
                    const ch = calculateCourseHandicap(
                      p?.handicapIndex || null,
                      t,
                    );
                    if (ch !== null && s?.status === "in") {
                      totalCh += ch;
                      validChCount++;
                    }
                    return { player: p, signup: s, tee: t, ch };
                  })
                  .filter((x) => x.player);

                const avgCh =
                  validChCount > 0
                    ? Math.round((totalCh / validChCount) * 10) / 10
                    : "-";

                return (
                  <div
                    key={group.id}
                    className="border border-slate-200 rounded-2xl overflow-hidden shadow-sm"
                  >
                    <div className="bg-slate-100 px-4 py-3 border-b border-slate-200 flex justify-between items-center">
                      <h4 className="font-black text-slate-800 uppercase tracking-widest text-xs">
                        {group.name}
                      </h4>
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                        Avg CH:{" "}
                        <span className="text-slate-800 font-black">
                          {avgCh}
                        </span>
                      </span>
                    </div>
                    <table className="w-full text-left text-[11px]">
                      <thead className="bg-slate-50 text-slate-400 font-black uppercase tracking-widest border-b border-slate-100">
                        <tr>
                          <th className="px-4 py-2">Player</th>
                          <th className="px-2 py-2">Tee</th>
                          <th className="px-2 py-2 text-center">CH</th>
                          <th className="px-4 py-2 text-right"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {groupPlayers.map(({ player, signup, tee, ch }) => {
                          const isOut = signup?.status === "out";
                          return (
                            <tr
                              key={player!.id}
                              className={`hover:bg-slate-50 ${isOut ? "opacity-40 line-through" : ""}`}
                            >
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() =>
                                      handleRemovePlayerFromRound(
                                        round.id,
                                        player!.id,
                                      )
                                    }
                                    className="text-slate-300 hover:text-rose-500 transition-colors text-xs font-bold"
                                    title="Remove Player"
                                  >
                                    ✕
                                  </button>
                                  <div className="font-black text-slate-700">
                                    {player!.name}
                                    <span className="text-slate-400 font-bold ml-1 text-[9px]">
                                      ({player!.handicapIndex ?? "NH"})
                                    </span>
                                  </div>
                                </div>
                                {isOut && (
                                  <div className="text-[9px] text-rose-500 font-bold uppercase tracking-widest mt-0.5">
                                    Dropped Out
                                  </div>
                                )}
                              </td>
                              <td className="px-2 py-3">
                                {tee ? (
                                  <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-[9px] font-black uppercase border border-emerald-100">
                                    {tee.name}
                                  </span>
                                ) : (
                                  <span className="text-slate-300 italic">
                                    ?
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-3 text-center font-black text-slate-900">
                                {ch ?? "-"}
                              </td>
                              <td className="px-4 py-3 text-right">
                                {round.groupingState === "draft" && (
                                  <select
                                    value={group.id}
                                    onChange={(e) =>
                                      handleMovePlayer(
                                        round.id,
                                        player!.id,
                                        e.target.value,
                                      )
                                    }
                                    className="text-[9px] font-bold uppercase tracking-widest bg-slate-100 border border-slate-200 rounded p-1 text-slate-600 outline-none"
                                  >
                                    <option value="unassigned">Bench</option>
                                    {round.groups!.map((g) => (
                                      <option key={g.id} value={g.id}>
                                        {g.name}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {groupPlayers.length === 0 && (
                          <tr>
                            <td
                              colSpan={3}
                              className="px-4 py-4 text-center text-slate-400 italic text-xs"
                            >
                              Empty Group
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>

            {/* Unassigned Bench */}
            {(() => {
              const assignedIds = new Set(
                round.groups.flatMap((g) => g.playerIds),
              );
              const unassigned = attendees.filter(
                (s) => !assignedIds.has(s.playerId),
              );
              if (unassigned.length === 0) return null;

              return (
                <div className="border border-amber-200 rounded-2xl overflow-hidden shadow-sm bg-amber-50/30">
                  <div className="bg-amber-100/50 px-4 py-3 border-b border-amber-200 flex justify-between items-center">
                    <h4 className="font-black text-amber-900 uppercase tracking-widest text-xs">
                      Unassigned Bench
                    </h4>
                    <span className="text-[10px] font-bold text-amber-700 uppercase tracking-widest">
                      {unassigned.length} Players
                    </span>
                  </div>
                  <table className="w-full text-left text-[11px]">
                    <thead className="bg-amber-100/30 text-amber-700/50 font-black uppercase tracking-widest border-b border-amber-100/50">
                      <tr>
                        <th className="px-4 py-2">Player</th>
                        <th className="px-2 py-2">Tee</th>
                        <th className="px-2 py-2 text-center">CH</th>
                        <th className="px-4 py-2 text-right"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-100/50">
                      {unassigned.map((signup) => {
                        const player = players.find(
                          (p) => p.id === signup.playerId,
                        );
                        const tee = course?.tees.find(
                          (t) => t.id === signup.teeId,
                        );
                        const ch = calculateCourseHandicap(
                          player?.handicapIndex || null,
                          tee,
                        );
                        return (
                          <tr
                            key={signup.playerId}
                            className="hover:bg-amber-50"
                          >
                            <td className="px-4 py-3 font-black text-slate-700">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() =>
                                    handleRemovePlayerFromRound(
                                      round.id,
                                      signup.playerId,
                                    )
                                  }
                                  className="text-slate-300 hover:text-rose-500 transition-colors text-xs font-bold"
                                  title="Remove Player"
                                >
                                  ✕
                                </button>
                                <div>
                                  {player?.name || "Unknown"}
                                  <span className="text-slate-400 font-bold ml-1 text-[9px]">
                                    ({player?.handicapIndex ?? "NH"})
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="px-2 py-3">
                              {tee ? (
                                <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-[9px] font-black uppercase border border-emerald-100">
                                  {tee.name}
                                </span>
                              ) : (
                                <span className="text-slate-300 italic">?</span>
                              )}
                            </td>
                            <td className="px-2 py-3 text-center font-black text-slate-900">
                              {ch ?? "-"}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {round.groupingState === "draft" && (
                                <select
                                  value="unassigned"
                                  onChange={(e) =>
                                    handleMovePlayer(
                                      round.id,
                                      signup.playerId,
                                      e.target.value,
                                    )
                                  }
                                  className="text-[9px] font-bold uppercase tracking-widest bg-white border border-amber-200 rounded p-1 text-amber-800 outline-none"
                                >
                                  <option value="unassigned">Bench</option>
                                  {round.groups!.map((g) => (
                                    <option key={g.id} value={g.id}>
                                      {g.name}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}

            {/* Waitlist */}
            {(() => {
              const waitlist = allRoundSignups
                .filter((s) => s.status === "waitlist")
                .sort((a, b) => a.updatedAt - b.updatedAt);

              if (waitlist.length === 0) return null;

              return (
                <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-sm bg-slate-50/30 mt-4">
                  <div className="bg-slate-100/50 px-4 py-3 border-b border-slate-200 flex justify-between items-center">
                    <h4 className="font-black text-slate-700 uppercase tracking-widest text-xs">
                      Waitlist
                    </h4>
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                      {waitlist.length} Players
                    </span>
                  </div>
                  <table className="w-full text-left text-[11px]">
                    <thead className="bg-slate-100/30 text-slate-500/50 font-black uppercase tracking-widest border-b border-slate-100/50">
                      <tr>
                        <th className="px-4 py-2">Player</th>
                        <th className="px-2 py-2">Tee</th>
                        <th className="px-4 py-2 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100/50">
                      {waitlist.map((signup) => {
                        const player = players.find(
                          (p) => p.id === signup.playerId,
                        );
                        const tee = course?.tees.find(
                          (t) => t.id === signup.teeId,
                        );
                        return (
                          <tr
                            key={signup.playerId}
                            className="hover:bg-slate-50"
                          >
                            <td className="px-4 py-3 font-black text-slate-700">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() =>
                                    handleRemovePlayerFromRound(
                                      round.id,
                                      signup.playerId,
                                    )
                                  }
                                  className="text-slate-300 hover:text-rose-500 transition-colors text-xs font-bold"
                                  title="Remove Player"
                                >
                                  ✕
                                </button>
                                <div>{player?.name || "Unknown"}</div>
                              </div>
                            </td>
                            <td className="px-2 py-3">
                              {tee ? (
                                <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-[9px] font-black uppercase border border-emerald-100">
                                  {tee.name}
                                </span>
                              ) : (
                                <span className="text-slate-300 italic">?</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() =>
                                  handlePromotePlayer(round.id, signup.playerId)
                                }
                                className="text-[9px] font-bold uppercase tracking-widest bg-emerald-50 border border-emerald-200 rounded px-2 py-1 text-emerald-700 hover:bg-emerald-100 transition-colors"
                              >
                                Promote
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-between items-center px-1">
              <h4 className="font-black text-slate-700 uppercase tracking-widest text-xs">
                Signups ({attendees.length}/{round.maxPlayers})
              </h4>
            </div>
            <div className="overflow-hidden rounded-2xl border border-slate-50">
              <table className="w-full text-left text-[11px]">
                <thead className="bg-slate-50 text-slate-400 font-black uppercase tracking-widest">
                  <tr>
                    <th className="px-3 py-3 sm:px-6 sm:py-4 border-b">
                      Player
                    </th>
                    <th className="px-3 py-3 sm:px-6 sm:py-4 border-b">Tee</th>
                    <th className="px-3 py-3 sm:px-6 sm:py-4 border-b text-center">
                      HI
                    </th>
                    <th className="px-3 py-3 sm:px-6 sm:py-4 border-b text-center">
                      CH
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {attendees.length > 0 ? (
                    attendees.map((signup: Signup) => {
                      const player = players.find(
                        (p: Player) => p.id === signup.playerId,
                      );
                      const tee = course?.tees.find(
                        (t: TeeInfo) => t.id === signup.teeId,
                      );
                      const ch = calculateCourseHandicap(
                        player?.handicapIndex || null,
                        tee,
                      );
                      return (
                        <tr
                          key={signup.playerId}
                          className="hover:bg-emerald-50/20"
                        >
                          <td className="px-3 py-3 sm:px-6 sm:py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() =>
                                  handleRemovePlayerFromRound(
                                    round.id,
                                    signup.playerId,
                                  )
                                }
                                className="text-slate-300 hover:text-rose-500 transition-colors text-xs font-bold"
                                title="Remove Player"
                              >
                                ✕
                              </button>
                              <div className="font-black text-slate-700 truncate max-w-[100px] sm:max-w-none">
                                {player?.name || "Unknown"}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4">
                            {tee ? (
                              <span className="bg-emerald-50 text-emerald-700 px-2 py-1 sm:px-3 sm:py-1 rounded-full text-[9px] font-black uppercase border border-emerald-100 whitespace-nowrap">
                                {tee.name}
                              </span>
                            ) : (
                              <span className="text-slate-300 italic">
                                Not set
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 text-center font-black text-slate-900 text-sm sm:text-lg">
                            {player?.handicapIndex ?? "-"}
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 text-center font-black text-emerald-700 text-sm sm:text-lg">
                            {ch ?? "-"}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-6 py-10 text-center text-slate-300 italic font-bold"
                      >
                        Waiting for players to join...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Main View Waitlist Section */}
        {!isGrouped &&
          (() => {
            const waitlist = allRoundSignups
              .filter((s) => s.status === "waitlist")
              .sort((a, b) => a.updatedAt - b.updatedAt);

            if (waitlist.length === 0) return null;

            return (
              <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
                <div className="bg-slate-100/50 px-4 py-3 border-b border-slate-200 flex justify-between items-center">
                  <h4 className="font-black text-slate-700 uppercase tracking-widest text-xs">
                    Waitlist
                  </h4>
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                    {waitlist.length} Players
                  </span>
                </div>
                <table className="w-full text-left text-[11px]">
                  <thead className="bg-slate-50 text-slate-400 font-black uppercase tracking-widest">
                    <tr>
                      <th className="px-3 py-3 sm:px-6 sm:py-4 border-b">
                        Player
                      </th>
                      <th className="px-3 py-3 sm:px-6 sm:py-4 border-b">
                        Tee
                      </th>
                      <th className="px-3 py-3 sm:px-6 sm:py-4 border-b text-center">
                        HI
                      </th>
                      <th className="px-3 py-3 sm:px-6 sm:py-4 border-b text-right">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {waitlist.map((signup) => {
                      const player = players.find(
                        (p) => p.id === signup.playerId,
                      );
                      const tee = course?.tees.find(
                        (t) => t.id === signup.teeId,
                      );
                      return (
                        <tr key={signup.playerId} className="hover:bg-slate-50">
                          <td className="px-3 py-3 sm:px-6 sm:py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() =>
                                  handleRemovePlayerFromRound(
                                    round.id,
                                    signup.playerId,
                                  )
                                }
                                className="text-slate-300 hover:text-rose-500 transition-colors text-xs font-bold"
                                title="Remove Player"
                              >
                                ✕
                              </button>
                              <div className="font-black text-slate-700 truncate max-w-[100px] sm:max-w-none">
                                {player?.name || "Unknown"}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4">
                            {tee ? (
                              <span className="bg-emerald-50 text-emerald-700 px-2 py-1 sm:px-3 sm:py-1 rounded-full text-[9px] font-black uppercase border border-emerald-100 whitespace-nowrap">
                                {tee.name}
                              </span>
                            ) : (
                              <span className="text-slate-300 italic">
                                Not set
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 text-center font-black text-slate-900 text-sm sm:text-lg">
                            {player?.handicapIndex ?? "-"}
                          </td>
                          <td className="px-3 py-3 sm:px-6 sm:py-4 text-right">
                            <button
                              onClick={() =>
                                handlePromotePlayer(round.id, signup.playerId)
                              }
                              className="text-[9px] font-bold uppercase tracking-widest bg-emerald-50 border border-emerald-200 rounded px-3 py-2 text-emerald-700 hover:bg-emerald-100 transition-colors"
                            >
                              Promote
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Portal Output for Print Preview */}
      {renderPrintPreview()}
      {/* Portal Output for Edit Player Modal */}
      {renderEditPlayerModal()}
      {/* Portal Output for Edit Round Modal */}
      {renderEditRoundModal()}
      {/* Portal Output for Edit Course Modal */}
      {renderEditCourseModal()}

      <div className="flex border-b border-slate-100 overflow-x-auto no-scrollbar pb-1">
        {[
          { id: "rounds", label: "Rounds", icon: "📅" },
          { id: "players", label: "Players", icon: "👥" },
          { id: "courses", label: "Courses", icon: "⛳" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex-1 min-w-[100px] py-5 px-4 text-xs font-black uppercase tracking-[0.2em] transition-all border-b-4 flex flex-col items-center gap-2 ${activeTab === tab.id ? "border-emerald-600 text-emerald-800 bg-emerald-50/50" : "border-transparent text-slate-400 hover:text-slate-600"}`}
          >
            <span className="text-lg">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "rounds" && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <form
            onSubmit={handleAddRound}
            className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 space-y-6"
          >
            <h3 className="font-black text-slate-800 uppercase tracking-widest text-xs border-l-4 border-emerald-500 pl-3">
              Schedule New Round
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Select Course
                </label>
                <select
                  value={courseId}
                  onChange={(e) => {
                    setCourseId(e.target.value);
                    setAssignedTeeId("");
                  }}
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                  required
                >
                  <option value="">Choose Course...</option>
                  {courses.map((c: Course) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Assigned Tee
                </label>
                <select
                  value={assignedTeeId}
                  onChange={(e) => setAssignedTeeId(e.target.value)}
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                  disabled={!courseId}
                >
                  <option value="">Open</option>
                  {courseId &&
                    courses
                      .find((c) => c.id === courseId)
                      ?.tees.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Play Date
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Tee Time
                </label>
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Buy-In ($)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-black">
                    $
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="Optional"
                    value={buyIn}
                    onChange={(e) => setBuyIn(e.target.value)}
                    className="w-full p-4 pl-8 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Max Players
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  required
                  value={maxPlayers}
                  onChange={(e) => setMaxPlayers(e.target.value)}
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                />
              </div>
            </div>
            <button
              type="submit"
              className="w-full bg-emerald-700 text-white font-black py-5 rounded-2xl hover:bg-emerald-800 transition-all shadow-xl uppercase text-sm tracking-[0.2em]"
            >
              Publish Round
            </button>
          </form>

          <div className="space-y-6">
            {upcomingRounds.length === 0 ? (
              <div className="bg-white p-20 rounded-3xl border-2 border-dashed border-slate-200 text-center">
                <p className="text-slate-400 font-bold italic">
                  No upcoming rounds scheduled.
                </p>
              </div>
            ) : (
              upcomingRounds.map(renderRoundCard)
            )}

            {completedRounds.length > 0 && (
              <div className="pt-4">
                <button
                  onClick={() => setShowCompleted(!showCompleted)}
                  className="w-full flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-slate-200 text-slate-600 font-bold uppercase text-xs tracking-widest hover:text-emerald-700 hover:border-emerald-200 transition-all"
                >
                  <span>Completed Rounds ({completedRounds.length})</span>
                  <span
                    className={`transform transition-transform duration-200 ${showCompleted ? "rotate-180" : ""}`}
                  >
                    ▼
                  </span>
                </button>
                {showCompleted && (
                  <div className="space-y-6 mt-6 animate-in fade-in slide-in-from-top-2 duration-300">
                    {completedRounds.map(renderRoundCard)}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "players" && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <form
            onSubmit={handleAddPlayer}
            className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 space-y-6"
          >
            <h3 className="font-black text-slate-800 uppercase tracking-widest text-xs border-l-4 border-emerald-500 pl-3">
              Add Roster Profile
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Full Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. Tiger Woods"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Mobile Phone
                </label>
                <input
                  type="tel"
                  placeholder="+15551234567"
                  value={playerPhone}
                  onChange={(e) => setPlayerPhone(e.target.value)}
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Handicap Index
                </label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="e.g. 10.4"
                  value={handicap}
                  onChange={(e) => setHandicap(e.target.value)}
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  GHIN #
                </label>
                <input
                  type="text"
                  placeholder="Optional"
                  value={ghinNumber}
                  onChange={(e) => setGhinNumber(e.target.value)}
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 text-sm font-black text-slate-700"
                />
              </div>
              <div className="space-y-2 md:col-span-2 flex items-center gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isAdmin"
                    checked={isAdmin}
                    onChange={(e) => setIsAdmin(e.target.checked)}
                    className="w-5 h-5 rounded text-emerald-600 focus:ring-emerald-500"
                  />
                  <label
                    htmlFor="isAdmin"
                    className="text-xs font-black text-slate-700 uppercase tracking-widest"
                  >
                    Administrator Access
                  </label>
                </div>
                {isAdmin && (
                  <input
                    type="text"
                    maxLength={4}
                    placeholder="Set 4-digit PIN"
                    value={adminPin}
                    onChange={(e) => setAdminPin(e.target.value)}
                    className="flex-1 p-2 bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500/20 text-sm font-black text-slate-700 text-center tracking-[0.2em]"
                    required
                  />
                )}
              </div>
            </div>
            <button
              type="submit"
              className="w-full bg-emerald-700 text-white font-black py-5 rounded-2xl hover:bg-emerald-800 shadow-xl uppercase tracking-widest text-xs transition-all"
            >
              Save to Roster
            </button>
          </form>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {players.map((player: Player) => (
              <div
                key={player.id}
                className={`bg-white p-6 rounded-[2rem] shadow-sm border border-slate-100 flex justify-between items-center group transition-all ${player.phoneNumber === authenticatedUserPhone ? "ring-2 ring-emerald-500" : ""}`}
              >
                <div className="flex items-center gap-5">
                  <div
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center font-black text-lg shadow-inner ${player.uid ? "bg-emerald-100 text-emerald-800" : "bg-slate-50 text-slate-300"}`}
                  >
                    {getInitials(player.name)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-black text-slate-800 text-base tracking-tight">
                        {player.name}
                        <span className="text-slate-400 font-bold ml-1 text-sm">
                          ({player.handicapIndex ?? "NH"})
                        </span>
                      </p>
                      {player.isAdmin && (
                        <span className="bg-amber-100 text-amber-800 text-[8px] px-2 py-0.5 rounded-full font-black uppercase tracking-widest">
                          Admin
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 font-bold tracking-tight mt-0.5">
                      {formatPhoneNumber(player.phoneNumber)}
                    </p>
                    <div className="flex items-center gap-3 mt-2">
                      <button
                        onClick={() => handleToggleAdmin(player.id)}
                        className={`text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-lg border ${player.isAdmin ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-50 text-slate-400 border-slate-100 hover:border-emerald-200 hover:text-emerald-700"}`}
                      >
                        {player.isAdmin ? "Revoke Admin" : "Make Admin"}
                      </button>
                      {player.isAdmin && (
                        <button
                          onClick={() => handleChangePin(player.id)}
                          className="text-[9px] text-slate-400 hover:text-emerald-600 font-black uppercase tracking-widest"
                        >
                          Change PIN
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEditPlayer(player)}
                    className="p-2 text-xl text-slate-400 hover:text-emerald-600 transition-colors"
                  >
                    ✎
                  </button>
                  <button
                    onClick={() => handleDeletePlayer(player.id)}
                    disabled={player.isAdmin && adminCount === 1}
                    className={`p-2 text-xl transition-colors ${player.isAdmin && adminCount === 1 ? "opacity-20 cursor-not-allowed" : "text-slate-400 hover:text-rose-500"}`}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "courses" && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 space-y-6">
            <h3 className="font-black text-slate-800 uppercase tracking-widest text-xs border-l-4 border-emerald-500 pl-3">
              Course Library
            </h3>
            <form
              onSubmit={handleCreateCourseShell}
              className="flex flex-col sm:flex-row gap-4"
            >
              <input
                type="text"
                placeholder="Course Name"
                value={newCourseName}
                onChange={(e) => setNewCourseName(e.target.value)}
                className="flex-1 p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none focus:ring-4 focus:ring-emerald-500/10 font-black text-sm text-slate-700"
                required
              />
              <button
                type="submit"
                className="bg-emerald-700 text-white font-black px-10 py-4 rounded-2xl hover:bg-emerald-800 shadow-xl uppercase text-xs tracking-widest transition-all"
              >
                Add Course
              </button>
            </form>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {courses.map((course: Course) => (
              <div
                key={course.id}
                className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-100 space-y-6 flex flex-col"
              >
                <div className="flex justify-between items-start">
                  <h4 className="font-black text-slate-900 uppercase tracking-widest text-sm">
                    {course.name}
                  </h4>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEditCourse(course)}
                      className="p-2 text-xl text-slate-400 hover:text-emerald-600 transition-colors"
                    >
                      ✎
                    </button>
                    <button
                      onClick={() =>
                        onUpdateCourses(
                          courses.filter((c: Course) => c.id !== course.id),
                        )
                      }
                      className="p-2 text-xl text-slate-400 hover:text-rose-600 transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                <div className="space-y-2 flex-grow">
                  {course.tees.length === 0 ? (
                    <p className="text-[11px] italic text-slate-300 font-bold mb-4">
                      No markers defined.
                    </p>
                  ) : (
                    course.tees.map((t: TeeInfo) => (
                      <div
                        key={t.id}
                        className="flex justify-between items-center text-[10px] bg-slate-50 px-4 py-3 rounded-xl border border-slate-50 group"
                      >
                        <span className="font-black text-emerald-800 uppercase tracking-widest">
                          {t.name}
                        </span>
                        <div className="flex items-center gap-3">
                          <div className="text-slate-500 font-black space-x-2">
                            {t.yardage && (
                              <>
                                <span>Y:{t.yardage}</span>
                                <span className="opacity-20">|</span>
                              </>
                            )}
                            <span>R:{t.rating}</span>
                            <span className="opacity-20">|</span>
                            <span>S:{t.slope}</span>
                            <span className="opacity-20">|</span>
                            <span>P:{t.par}</span>
                          </div>
                          <button
                            onClick={() => handleDeleteTee(course.id, t.id)}
                            className="text-slate-400 hover:text-rose-500 font-bold px-1"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">
                    Add Tee Box
                  </h5>
                  <div className="grid grid-cols-5 gap-2 mb-2">
                    <input
                      placeholder="Tee Name"
                      className="p-2 bg-slate-50 rounded-lg text-[10px] font-bold outline-none focus:ring-2 focus:ring-emerald-100 col-span-5"
                      value={teeInputs[course.id]?.name || ""}
                      onChange={(e) =>
                        handleTeeInputChange(course.id, "name", e.target.value)
                      }
                    />
                    <input
                      type="number"
                      placeholder="Yardage"
                      className="p-2 bg-slate-50 rounded-lg text-[10px] font-bold outline-none focus:ring-2 focus:ring-emerald-100"
                      value={teeInputs[course.id]?.yardage || ""}
                      onChange={(e) =>
                        handleTeeInputChange(
                          course.id,
                          "yardage",
                          e.target.value,
                        )
                      }
                    />
                    <input
                      type="number"
                      step="0.1"
                      placeholder="Rating"
                      className="p-2 bg-slate-50 rounded-lg text-[10px] font-bold outline-none focus:ring-2 focus:ring-emerald-100"
                      value={teeInputs[course.id]?.rating || ""}
                      onChange={(e) =>
                        handleTeeInputChange(
                          course.id,
                          "rating",
                          e.target.value,
                        )
                      }
                    />
                    <input
                      type="number"
                      placeholder="Slope"
                      className="p-2 bg-slate-50 rounded-lg text-[10px] font-bold outline-none focus:ring-2 focus:ring-emerald-100"
                      value={teeInputs[course.id]?.slope || ""}
                      onChange={(e) =>
                        handleTeeInputChange(course.id, "slope", e.target.value)
                      }
                    />
                    <input
                      type="number"
                      placeholder="Par"
                      className="p-2 bg-slate-50 rounded-lg text-[10px] font-bold outline-none focus:ring-2 focus:ring-emerald-100"
                      value={teeInputs[course.id]?.par || ""}
                      onChange={(e) =>
                        handleTeeInputChange(course.id, "par", e.target.value)
                      }
                    />
                    <button
                      onClick={() => handleAddTeeToCourse(course.id)}
                      className="bg-emerald-100 text-emerald-700 font-black text-[10px] py-2 rounded-lg uppercase tracking-widest hover:bg-emerald-200 transition-colors col-span-1 flex items-center justify-center"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
